import React, { useState, useEffect } from "react";
import { Link, useRouter } from "../../../util/router";
import Meta from "../../../components/Meta";
import Section from "../../../components/Section";
import { useAuth } from "../../../util/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "../../../util/firebase";
import { apiRequestNoAuth } from "../../../util/util";
import PasswordSetupModal from "../../../components/Custom/PasswordSetupModal";
import PageLoader from "../../../components/PageLoader";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { trackPurchase } from "../../../util/analytics";

const functions = getFunctions(firebaseApp);

function AgencyTrainingOnboarding(props) {
  const router = useRouter();
  const auth = useAuth();

  // Email the buyer used at checkout: the signed-in account, the value the
  // checkout page stashed before redirecting to Stripe, or the lead-capture
  // record from the embedded flow.
  const getBuyerEmail = () => {
    if (auth.user && auth.user.email) return auth.user.email;
    try {
      const stashed = sessionStorage.getItem('iat_checkout_email');
      if (stashed) return stashed;
      const lead = JSON.parse(localStorage.getItem('iat_lead_data') || 'null');
      if (lead && lead.email) return lead.email;
    } catch (e) { /* storage unavailable */ }
    return null;
  };
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [setupToken, setSetupToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  // Track Purchase event on mount (before token handling strips URL params)
  useEffect(() => {
    if (router.query.paid === 'true' && router.query.product) {
      trackPurchase(router.query.product);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Token recovery state (T-011.19)
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [requestingNewLink, setRequestingNewLink] = useState(false);
  const [newLinkMessage, setNewLinkMessage] = useState(null);

  useEffect(() => {
    const handleTokenSignin = async () => {
      let token = router.query.token;
      const sessionId = router.query.session_id;

      // T-011.21: If we have session_id but no token, fetch token from session
      // Note: Webhook might still be processing, so retry a few times
      if (!token && sessionId) {
        // The endpoint requires the buyer's email as proof of possession, not
        // just the session id from the URL. Signed-in buyers have it on their
        // account; guests stored it on the checkout page before redirecting.
        const buyerEmail = getBuyerEmail();
        if (!buyerEmail) {
          console.log('T-011.21: No buyer email available for session lookup; use the emailed link');
          return;
        }
        setSigningIn(true);
        const maxRetries = 5;
        const retryDelay = 2000; // 2 seconds

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            console.log(`T-011.21: Fetching token (attempt ${attempt}/${maxRetries})...`);
            const response = await apiRequestNoAuth('get-password-token-by-session', 'POST', {
              sessionId: sessionId,
              email: buyerEmail,
            });

            if (response && response.token) {
              token = response.token;
              console.log('T-011.21: Got password token from session:', token.substring(0, 8) + '...');
              break;
            } else if (attempt < maxRetries) {
              // Token not found yet, wait and retry (webhook may still be processing)
              console.log(`T-011.21: Token not found, retrying in ${retryDelay}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
          } catch (error) {
            if (attempt < maxRetries && error.code === 'token_not_found') {
              // Not found, webhook may still be processing
              console.log(`T-011.21: Token not ready, retrying in ${retryDelay}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            } else {
              console.error('T-011.21: Error fetching token by session:', error);
              break;
            }
          }
        }

        if (!token) {
          // No token found after retries - user may already have password set
          console.log('T-011.21: No password token found for session after retries, may already be set up');
          setSigningIn(false);
          return;
        }
      }

      if (!token) {
        // No token - normal page view
        return;
      }

      setSigningIn(true);

      try {
        // Validate token and get custom token for auto-signin
        const validateToken = httpsCallable(functions, 'validatePasswordSetupToken');
        const result = await validateToken({ token });

        if (!result.data.valid) {
          throw new Error('Invalid token');
        }

        // Auto-signin with custom token
        await auth.signInWithCustomToken(result.data.customToken);

        // Store token for password modal
        setSetupToken(token);
        setShowPasswordModal(true);

        // Clear token/session_id from URL (security)
        router.replace(router.pathname, { shallow: true });

        setSigningIn(false);

      } catch (error) {
        console.error('Token validation error:', error);

        // If token already used, redirect to sign in - they already have a password
        if (error.code === 'functions/failed-precondition') {
          console.log('Token already used, redirecting to sign in...');
          router.push('/auth/signin?message=already_setup');
          return;
        }

        let errorMessage = 'Invalid or expired setup link. Please check your email for the correct link.';

        if (error.code === 'functions/deadline-exceeded') {
          errorMessage = 'Your setup link has expired. Please check your email for a new link.';
        } else if (error.code === 'functions/resource-exhausted') {
          errorMessage = 'Too many attempts. Please request a new link.';
        } else if (error.code === 'functions/not-found') {
          errorMessage = 'Invalid setup link. Please check your email for the correct link.';
        }

        setTokenError(errorMessage);
        setSigningIn(false);
      }
    };

    handleTokenSignin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.token, router.query.session_id]);

  const handlePasswordSetupComplete = () => {
    setShowPasswordModal(false);
    setSetupToken(null);
    // Stay on confirmation page so user can read it
    // They can click "Go to Dashboard" when ready
  };

  // Check if the error is recoverable (expired or too many attempts)
  const isRecoverableError = tokenError && (
    tokenError.includes('expired') ||
    tokenError.includes('attempts') ||
    tokenError.includes('request a new link')
  );

  // Token recovery handler (T-011.19)
  const handleRequestNewLink = async (e) => {
    e.preventDefault();

    if (!recoveryEmail.trim()) {
      setNewLinkMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    setRequestingNewLink(true);
    setNewLinkMessage(null);

    try {
      const regenerateToken = httpsCallable(functions, 'regeneratePasswordSetupToken');
      const result = await regenerateToken({ email: recoveryEmail.trim() });

      setNewLinkMessage({
        type: 'success',
        text: result.data.message
      });
      setShowRecoveryForm(false);
    } catch (error) {
      console.error('Token regeneration error:', error);

      let errorMsg = 'Failed to send new link. Please try again.';
      if (error.code === 'functions/resource-exhausted') {
        errorMsg = 'Too many requests. Please try again in an hour.';
      } else if (error.code === 'functions/failed-precondition') {
        errorMsg = error.message || 'Password has already been set. Please sign in normally.';
      }

      setNewLinkMessage({ type: 'error', text: errorMsg });
    } finally {
      setRequestingNewLink(false);
    }
  };

  if (signingIn) {
    return <PageLoader />;
  }

  return (
    <>
      <Meta title="Welcome to Agency Training" />

      {tokenError && (
        <div className="max-w-2xl mx-auto mt-6 mb-0 px-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 mb-3">{tokenError}</p>

            {isRecoverableError && !showRecoveryForm && newLinkMessage?.type !== 'success' && (
              <button
                onClick={() => setShowRecoveryForm(true)}
                className="text-green-700 underline hover:text-green-800"
              >
                Request a new setup link
              </button>
            )}

            {showRecoveryForm && (
              <form onSubmit={handleRequestNewLink} className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter your email to receive a new setup link:
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={recoveryEmail}
                    onChange={(e) => setRecoveryEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    disabled={requestingNewLink}
                  />
                  <button
                    type="submit"
                    disabled={requestingNewLink}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {requestingNewLink ? 'Sending...' : 'Send Link'}
                  </button>
                </div>
              </form>
            )}

            {newLinkMessage && (
              <p className={`mt-3 ${newLinkMessage.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                {newLinkMessage.text}
              </p>
            )}
          </div>
        </div>
      )}

      <Section
        size="md"
        bgColor="bg-white"
        textColor="text-gray-900"
      >
        <div className="container max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <CheckCircleIcon className="h-20 w-20 text-green-700 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome to Agency Training!
            </h1>
            <p className="text-xl text-gray-600">
              Your purchase was successful. Let's get you started!
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              What's Next?
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Access Your Training Dashboard</h3>
                  <p className="text-gray-600">
                    Head to your dashboard to view all available training materials, videos, and resources.
                  </p>
                </div>
              </li>
              {/* Only show profile prompt if user hasn't completed it */}
              {auth.user && !auth.user.completeInfo && (
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Complete Your Profile</h3>
                    <p className="text-gray-600">
                      Fill out your profile information to personalize your experience.
                    </p>
                  </div>
                </li>
              )}
              <li className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Start Learning</h3>
                  <p className="text-gray-600">
                    Begin with our foundational courses and work your way through the curriculum.
                  </p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Join the Community</h3>
                  <p className="text-gray-600">
                    Connect with other insurance professionals and share your progress.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-gray-100 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              Need Help?
            </h2>
            <p className="text-gray-600 mb-4">
              If you have any questions or need assistance, our support team is here to help.
            </p>
            <Link to="/support">
              <button className="bg-green-700 text-white px-6 py-2 rounded-lg hover:bg-green-600">
                Visit Help Center
              </button>
            </Link>
          </div>

          <div className="text-center">
            <Link to="/dashboard/dashboard">
              <button className="bg-green-700 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-600">
                Go to Dashboard
              </button>
            </Link>
          </div>
        </div>
      </Section>

      {/* Password Setup Modal for new users */}
      <PasswordSetupModal
        isOpen={showPasswordModal}
        token={setupToken}
        onComplete={handlePasswordSetupComplete}
      />
    </>
  );
}

export default AgencyTrainingOnboarding;
